import React, { useMemo, useState } from 'react'
import {
  Grid,
  CircularProgress,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { withRouter } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import qs from 'query-string'

import useStyles from './styles'
import logo from '../../assets/logo.png'
import authApi from '../../api/authApi'
import { useUserDispatch } from '../../context/user/UserContext'
import Storages from '../../context/user/storages'

function validatePassword(password) {
  const errors = []
  if (!password || password.length < 12) errors.push('min-length')
  if (!/[A-Z]/.test(password || '')) errors.push('uppercase')
  if (!/[a-z]/.test(password || '')) errors.push('lowercase')
  if (!/[0-9]/.test(password || '')) errors.push('number')
  if (!/[^A-Za-z0-9]/.test(password || '')) errors.push('symbol')
  return errors
}

function AcceptInvitation({ history, location }) {
  const classes = useStyles()
  const { t } = useTranslation()
  const userDispatch = useUserDispatch()

  const { token, email } = useMemo(() => {
    const parsed = qs.parse(location.search)
    return { token: parsed.token || '', email: parsed.email || '' }
  }, [location.search])

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const passwordErrors = validatePassword(password)
  const isFormValid =
    token &&
    email &&
    passwordErrors.length === 0 &&
    password === confirmPassword

  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault()
    if (!isFormValid) return
    setError(null)
    setIsLoading(true)
    authApi
      .acceptInvitation(token, email, password)
      .then((res) => {
        const payload = res.data
        Storages.tokens.persist({
          token: payload.token,
          refreshToken: payload.refreshToken,
        })
        userDispatch({ type: 'LOGIN_SUCCESS', payload: { user: payload.user } })
        history.push('/')
      })
      .catch((err) => {
        const code =
          err && err.response && err.response.data && err.response.data.error
        const fallback =
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
          'errors.api-error'
        if (code === 'EXPIRED_TOKEN')
          setError(t('accept-invitation-view.expired'))
        else if (code === 'TOKEN_ALREADY_USED')
          setError(t('accept-invitation-view.already-used'))
        else if (code === 'INVALID_TOKEN')
          setError(t('accept-invitation-view.invalid'))
        else if (code === 'INVALID_PASSWORD') setError(fallback)
        else setError(t('errors.api-error'))
        setIsLoading(false)
      })
  }

  return (
    <Grid container justifyContent="center" className={classes.container}>
      <Grid
        item
        container
        xs={11}
        sm={6}
        md={4}
        lg={3}
        xl={3}
        className={classes.formContainer}
      >
        <Grid item container xs={10}>
          <Grid item xs={12} className={classes.logoContainer}>
            <img src={logo} alt="logo" className={classes.logo} />
          </Grid>
          <Grid item xs={12} className={classes.messageContainer}>
            <Typography variant="h6">
              {t('accept-invitation-view.title')}
            </Typography>
            <Typography variant="body2">
              {t('accept-invitation-view.message')}
            </Typography>
          </Grid>
          <form className={classes.form} onSubmit={submit}>
            <TextField
              variant="standard"
              id="email"
              value={email}
              margin="normal"
              label={t('login-view.email')}
              type="email"
              fullWidth
              disabled
            />
            <TextField
              variant="standard"
              id="password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((s) => !s)}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              label={t('accept-invitation-view.password')}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
            />
            <TextField
              variant="standard"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              label={t('accept-invitation-view.confirm-password')}
              type={showPassword ? 'text' : 'password'}
              error={confirmPassword.length > 0 && confirmPassword !== password}
              helperText={
                confirmPassword.length > 0 && confirmPassword !== password
                  ? t('common.password-not-match')
                  : ''
              }
              fullWidth
              required
            />
            <Typography className={classes.hint}>
              {t('accept-invitation-view.password-requirements')}
            </Typography>
            {error && (
              <Typography className={classes.error}>{error}</Typography>
            )}
            {!token || !email ? (
              <Typography className={classes.error}>
                {t('accept-invitation-view.missing-params')}
              </Typography>
            ) : null}
            <Grid item container className={classes.formButtons}>
              {isLoading ? (
                <CircularProgress size={26} className={classes.loader} />
              ) : (
                <Button
                  type="submit"
                  disabled={!isFormValid}
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                >
                  {t('accept-invitation-view.submit')}
                </Button>
              )}
            </Grid>
          </form>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default withRouter(AcceptInvitation)
